import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, FileText, Loader2, Sparkles } from 'lucide-react';
import { apiFetch } from '../lib/api';

/**
 * WIZARD DE PROPUESTA ASISTIDA (Sprint 4).
 *
 * Cuatro pasos y una regla: la IA redacta SOBRE el andamio determinista, nunca
 * en su lugar. Por eso el paso 3 dice de dónde sale cada cosa — el documento
 * final lleva los párrafos redactados y, debajo, la evidencia que los respalda.
 */

interface Opciones {
  formatos: { codigo: string; nombre: string; instruccion: string }[];
  jornadas: {
    sesion_id: string;
    fecha: string | null;
    tiene_sintesis: boolean;
    indice_trazabilidad: number | null;
  }[];
}

interface EstadoCorrida {
  corrida_id: string;
  estado: string;
  mensaje: string;
  posicion: number;
  eta_seg: number | null;
  propuesta_id?: string;
}

export function WizardPropuesta({
  procesoId,
  onListo,
  onCerrar,
}: {
  procesoId: string;
  onListo: () => void;
  onCerrar: () => void;
}) {
  const [paso, setPaso] = useState(1);
  const [objetivo, setObjetivo] = useState('');
  const [formato, setFormato] = useState('propuesta');
  const [jornadas, setJornadas] = useState<string[]>([]);
  const [corrida, setCorrida] = useState<EstadoCorrida | null>(null);

  const opciones = useQuery({
    queryKey: ['propuesta', 'opciones', procesoId],
    queryFn: () => apiFetch<Opciones>(`/procesos/${procesoId}/propuesta/opciones`),
  });

  // Al abrir, se preseleccionan las jornadas que ya tienen análisis: son las
  // únicas que aportan contexto, y elegirlas a mano una por una es fricción.
  const jornadasConSintesis = (opciones.data?.jornadas ?? []).filter((j) => j.tiene_sintesis);
  if (jornadas.length === 0 && jornadasConSintesis.length > 0 && paso === 1) {
    setJornadas(jornadasConSintesis.map((j) => j.sesion_id));
  }

  const generar = useMutation({
    mutationFn: () =>
      apiFetch<EstadoCorrida>(`/procesos/${procesoId}/propuesta/asistida`, {
        method: 'POST',
        json: { objetivo, formato, jornadas },
      }),
    onSuccess: (r) => {
      setCorrida(r);
      setPaso(3);
    },
  });

  // Mientras redacta, se consulta el estado: es una fila real, no una espera
  // fingida — puede haber otro análisis adelante.
  useQuery({
    queryKey: ['corrida', corrida?.corrida_id],
    queryFn: () => apiFetch<EstadoCorrida>(`/ai/corridas/${corrida!.corrida_id}`),
    enabled: Boolean(corrida?.corrida_id) && paso === 3,
    refetchInterval: 4000,
    onSuccess: (r: EstadoCorrida) => {
      setCorrida((prev) => ({ ...(prev as EstadoCorrida), ...r }));
      if (r.estado === 'ok') {
        setPaso(4);
        onListo();
      }
    },
  } as never);

  const puedeGenerar = objetivo.trim().length >= 10;

  return (
    <section className="rounded-xl border border-primary/40 bg-surface-raised p-5">
      <header className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold text-content">
          Armar la propuesta con IA
        </h2>
        <span className="flex-1" />
        <span className="font-sans text-xs text-content-muted">Paso {paso} de 4</span>
        <button
          type="button"
          onClick={onCerrar}
          className="font-sans text-xs text-content-muted underline hover:text-content"
        >
          Cerrar
        </button>
      </header>

      {/* Paso 1 · qué documento y para qué */}
      {paso === 1 && (
        <div className="mt-4">
          <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
            ¿Para qué es este documento? Escribilo como se lo explicarías a quien lo va a leer.
            <textarea
              value={objetivo}
              onChange={(e) => setObjetivo(e.target.value)}
              rows={3}
              placeholder="Definir el sistema del Consejo de la Carrera Judicial: qué debe hacer y con qué datos."
              className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
            />
          </label>
          <p className="mt-3 font-sans text-xs text-content-muted">Formato de salida</p>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {(opciones.data?.formatos ?? []).map((f) => (
              <button
                key={f.codigo}
                type="button"
                onClick={() => setFormato(f.codigo)}
                className={
                  formato === f.codigo
                    ? 'rounded-lg border-2 border-primary bg-surface-base px-3 py-2 text-left'
                    : 'rounded-lg border border-border bg-surface-base px-3 py-2 text-left hover:border-border-strong'
                }
              >
                <span className="block font-sans text-sm font-semibold text-content">{f.nombre}</span>
                <span className="mt-0.5 block font-sans text-[11px] leading-snug text-content-muted">
                  {f.instruccion}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              disabled={!puedeGenerar}
              onClick={() => setPaso(2)}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-lg bg-primary px-4 py-2 font-sans text-sm font-semibold text-white disabled:opacity-40"
            >
              Elegir jornadas <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Paso 2 · qué jornadas entran */}
      {paso === 2 && (
        <div className="mt-4">
          <p className="font-sans text-xs text-content-muted">
            Entran las jornadas que ya tienen su análisis. Las que no lo tienen aportan
            su evidencia, pero no su lectura: conviene analizarlas antes.
          </p>
          <ul className="mt-3 space-y-1.5">
            {(opciones.data?.jornadas ?? []).map((j) => (
              <li key={j.sesion_id}>
                <label className="flex items-center gap-2 rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content">
                  <input
                    type="checkbox"
                    checked={jornadas.includes(j.sesion_id)}
                    onChange={(e) =>
                      setJornadas((prev) =>
                        e.target.checked
                          ? [...prev, j.sesion_id]
                          : prev.filter((x) => x !== j.sesion_id),
                      )
                    }
                    className="h-4 w-4"
                  />
                  <span className="flex-1">{j.fecha ?? 'Sin fecha'}</span>
                  {j.tiene_sintesis ? (
                    <span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success">
                      analizada · {j.indice_trazabilidad ?? 0}% trazable
                    </span>
                  ) : (
                    <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-[11px] text-content-muted">
                      sin análisis
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-between">
            <button
              type="button"
              onClick={() => setPaso(1)}
              className="font-sans text-sm text-content-muted underline"
            >
              Volver
            </button>
            <button
              type="button"
              disabled={generar.isPending}
              onClick={() => generar.mutate()}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-lg bg-primary px-4 py-2 font-sans text-sm font-bold text-white disabled:opacity-40"
            >
              {generar.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              )}
              Redactar la propuesta
            </button>
          </div>
          {generar.isError && (
            <p className="mt-2 font-sans text-xs text-error">
              {(generar.error as { detail?: string })?.detail ?? 'No se pudo generar.'}
            </p>
          )}
        </div>
      )}

      {/* Paso 3 · la fila real */}
      {paso === 3 && corrida && (
        <div className="mt-4 rounded-lg border border-border bg-surface-base px-4 py-4">
          <p className="flex items-center gap-2 font-sans text-sm text-content">
            <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
            {corrida.mensaje}
          </p>
          {corrida.posicion > 0 && (
            <p className="mt-1 font-sans text-xs text-content-muted">
              Hay {corrida.posicion} análisis antes que el tuyo. El motor corre de a uno.
            </p>
          )}
          {corrida.eta_seg != null && (
            <p className="mt-1 font-sans text-xs text-content-muted">
              Suele tardar alrededor de {Math.round(corrida.eta_seg / 60)} minutos.
            </p>
          )}
          <p className="mt-3 font-sans text-[11px] text-content-muted">
            Podés cerrar esta ventana: el análisis sigue y queda guardado.
          </p>
        </div>
      )}

      {/* Paso 4 · listo */}
      {paso === 4 && (
        <div className="mt-4 rounded-lg border border-success/40 bg-surface-base px-4 py-4">
          <p className="flex items-center gap-2 font-sans text-sm font-semibold text-content">
            <Check className="h-4 w-4 text-success" aria-hidden="true" />
            La propuesta está redactada.
          </p>
          <p className="mt-1 font-sans text-xs text-content-muted">
            Cada párrafo lleva las citas de los aportes que lo respaldan, y debajo queda
            la evidencia original. Revisala y, cuando esté, congelala.
          </p>
          <button
            type="button"
            onClick={onCerrar}
            className="mt-3 inline-flex min-h-touch items-center gap-1.5 rounded-lg bg-primary px-4 py-2 font-sans text-sm font-semibold text-white"
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Ver la propuesta
          </button>
        </div>
      )}
    </section>
  );
}
